import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Calendar, MessageSquare, LogOut, ClipboardList, MapPin, Save, Phone, Lock, ArrowRight, Inbox, FileDown, Heart, Trash2 } from "lucide-react";
import PageTransition from "../components/PageTransition";
import api from "../lib/api";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { downloadReservationPdf } from "../lib/pdf";
import { resolveImageUrl } from "../lib/imageUrl";
import { lowestNightly } from "../lib/roomPricing";

const StatusBadge = ({ status }) => {
    const styles = {
        approved: "bg-emerald-900/40 text-emerald-300 border-emerald-600/40",
        pending: "bg-gold/15 text-gold border-gold/40",
        rejected: "bg-rose-900/40 text-rose-300 border-rose-500/40",
    };
    const label = { approved: "Onaylı", pending: "Beklemede", rejected: "Reddedildi" }[status] || status;
    return <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border ${styles[status] || ""}`}>{label}</span>;
};

const Profile = () => {
    const { customer, checked, logout, reload, toggleFavorite } = useCustomerAuth();
    const { formatPrice, settings } = useApp();
    const navigate = useNavigate();
    const [tab, setTab] = useState("reservations");
    const [reservations, setReservations] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [favHotels, setFavHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profileForm, setProfileForm] = useState({ name: "", phone: "", password: "" });

    useEffect(() => {
        if (customer) setProfileForm({ name: customer.name || "", phone: customer.phone || "", password: "" });
    }, [customer]);

    const loadData = () => {
        setLoading(true);
        Promise.all([
            api.get("/customer/reservations").then(({ data }) => setReservations(data)).catch(() => {}),
            api.get("/customer/reviews").then(({ data }) => setReviews(data)).catch(() => {}),
            api.get("/customer/favorites").then(({ data }) => setFavHotels(data.hotels || [])).catch(() => {}),
        ]).finally(() => setLoading(false));
    };
    useEffect(() => { if (customer) loadData(); }, [customer]);

    if (checked && !customer) return <Navigate to="/login" replace />;
    if (!customer) return null;

    const saveProfile = async () => {
        const body = {};
        if (profileForm.name && profileForm.name !== customer.name) body.name = profileForm.name;
        if (profileForm.phone !== customer.phone) body.phone = profileForm.phone;
        if (profileForm.password) body.password = profileForm.password;
        if (Object.keys(body).length === 0) { toast.info("Değişiklik yok"); return; }
        try {
            await api.patch("/customer/me", body);
            toast.success("Profil güncellendi");
            setProfileForm({ ...profileForm, password: "" });
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
        }
    };

    const doLogout = () => { logout(); toast.success("Çıkış yapıldı"); navigate("/"); };

    const TABS = [
        { id: "reservations", label: "Rezervasyonlarım", icon: ClipboardList, count: reservations.length },
        { id: "favorites", label: "Favorilerim", icon: Heart, count: favHotels.length },
        { id: "reviews", label: "Yorumlarım", icon: MessageSquare, count: reviews.length },
        { id: "profile", label: "Profilim", icon: User },
    ];

    const handleUnfav = async (hotelId) => {
        const res = await toggleFavorite(hotelId);
        if (res.ok) {
            toast.success("Favorilerden çıkarıldı");
            setFavHotels((arr) => arr.filter((h) => h.id !== hotelId));
        }
    };

    return (
        <PageTransition>
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
                <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Hesabım</div>
                        <h1 className="font-serif text-4xl lg:text-5xl">Merhaba, {customer.name}</h1>
                        <p className="text-sm text-muted_green mt-2">{customer.email}</p>
                    </div>
                    <button onClick={doLogout} className="btn-outline-gold !text-xs" data-testid="logout-btn">
                        <LogOut className="w-3.5 h-3.5" /> Çıkış Yap
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-px bg-border_green mb-6 overflow-x-auto">
                    {TABS.map((tb) => {
                        const Icon = tb.icon;
                        return (
                            <button key={tb.id} onClick={() => setTab(tb.id)}
                                className={`px-5 py-3 text-xs uppercase tracking-[0.18em] flex items-center gap-2 transition-colors flex-shrink-0 ${
                                    tab === tb.id ? "bg-gold text-emerald_deep font-bold" : "bg-emerald_surface text-bone/85 hover:bg-emerald_hover"
                                }`}
                                data-testid={`profile-tab-${tb.id}`}>
                                <Icon className="w-3.5 h-3.5" /> {tb.label}
                                {tb.count != null && <span className="text-[10px] opacity-75">({tb.count})</span>}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="text-muted_green text-sm">Yükleniyor…</div>
                ) : tab === "reservations" ? (
                    reservations.length === 0 ? (
                        <div className="border border-dashed border-border_green p-12 text-center" data-testid="my-reservations-empty">
                            <Inbox className="w-12 h-12 text-gold/60 mx-auto mb-3" />
                            <div className="font-serif text-xl mb-1">Henüz rezervasyonunuz yok</div>
                            <p className="text-sm text-muted_green mb-4">Bir otel seçerek hemen rezervasyon talebi oluşturun.</p>
                            <Link to="/#hotels" className="btn-gold !text-xs"><span>Otelleri Gör</span> <ArrowRight className="w-3.5 h-3.5" /></Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {reservations.some((r) => r.status === "approved") && (
                                <div className="border border-gold/40 bg-gold/10 p-4 flex items-start gap-3" data-testid="profile-pdf-hint">
                                    <FileDown className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-bone">
                                        <span className="font-semibold text-gold">PDF makbuzunuzu indirin.</span>{" "}
                                        Onaylı rezervasyonlarınızın resmî makbuzunu sağdaki <span className="font-mono">PDF</span> butonuyla bilgisayarınıza kaydedebilirsiniz. Makbuzda <strong>otele girişte ödenecek kalan tutar</strong> açıkça belirtilmiştir.
                                    </div>
                                </div>
                            )}
                            {reservations.map((r, i) => (
                                <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="border border-border_green bg-emerald_surface p-5" data-testid={`my-reservation-${r.id}`}>
                                    <div className="flex items-start justify-between flex-wrap gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span className="font-mono text-xs text-gold">{r.reservation_code}</span>
                                                <StatusBadge status={r.status} />
                                            </div>
                                            <Link to={`/hotels/${r.hotel_id}`} className="font-serif text-2xl hover:text-gold transition-colors">{r.hotel_name}</Link>
                                            <div className="text-xs text-muted_green mt-1.5 flex items-center gap-3 flex-wrap">
                                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(r.check_in).toLocaleDateString("tr-TR")} → {new Date(r.check_out).toLocaleDateString("tr-TR")} ({r.nights} gece)</span>
                                                <span>· {r.room_name} x {r.rooms_count}</span>
                                                <span>· {r.guests} misafir</span>
                                            </div>
                                            {r.rejection_reason && (
                                                <div className="mt-2 text-[11px] text-rose-400 border-l-2 border-rose-500/40 pl-2">Red sebebi: {r.rejection_reason}</div>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Toplam</div>
                                            <div className="font-serif text-2xl text-gold">{formatPrice(r.total_price)}</div>
                                            <div className="text-[10px] text-muted_green">Ön ödeme: {formatPrice(r.deposit_amount)}</div>
                                            <div className="text-[10px] text-gold font-semibold mt-0.5" data-testid={`remaining-at-hotel-${r.id}`}>
                                                Otelde kalan: {formatPrice(Math.max(0, (r.total_price || 0) - (r.deposit_amount || 0)))}
                                            </div>
                                            <button
                                                onClick={() => downloadReservationPdf({ reservation: r, settings, formatPrice })}
                                                className="mt-2 btn-outline-gold !text-[10px] !py-1.5 !px-2"
                                                data-testid={`download-pdf-${r.id}`}
                                            >
                                                <FileDown className="w-3 h-3" /> PDF
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )
                ) : tab === "favorites" ? (
                    favHotels.length === 0 ? (
                        <div className="border border-dashed border-border_green p-12 text-center" data-testid="my-favorites-empty">
                            <Heart className="w-12 h-12 text-gold/60 mx-auto mb-3" />
                            <div className="font-serif text-xl mb-1">Henüz favori otel eklemediniz</div>
                            <p className="text-sm text-muted_green mb-4">Otel kartlarındaki kalp ikonuna tıklayarak favori listenize ekleyebilirsiniz.</p>
                            <Link to="/#hotels" className="btn-gold !text-xs"><span>Otelleri Gör</span> <ArrowRight className="w-3.5 h-3.5" /></Link>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-3" data-testid="my-favorites-list">
                            {favHotels.map((h) => {
                                const lowest = lowestNightly(h);
                                return (
                                    <div key={h.id} className="border border-border_green bg-emerald_surface overflow-hidden group" data-testid={`my-favorite-${h.id}`}>
                                        <Link to={`/hotels/${h.id}`} className="block relative h-44 overflow-hidden">
                                            <img src={resolveImageUrl(h.images?.[0])} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-emerald_deep/85 to-transparent" />
                                            <div className="absolute bottom-3 left-3 right-3">
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-1">{h.city === "Mecca" ? "Mekke" : "Medine"}</div>
                                                <div className="font-serif text-xl text-bone leading-tight">{h.name}</div>
                                            </div>
                                        </Link>
                                        <div className="p-4 flex items-center justify-between gap-2">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Gece başına</div>
                                                <div className="font-serif text-xl text-gold">{lowest ? formatPrice(lowest) : "—"}</div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Link to={`/hotels/${h.id}`} className="btn-gold !text-[11px] !py-2 !px-3">
                                                    Rezerve Et <ArrowRight className="w-3 h-3" />
                                                </Link>
                                                <button onClick={() => handleUnfav(h.id)} className="text-rose-400 hover:text-rose-500 p-2 border border-rose-500/40" aria-label="Favorilerden çıkar" data-testid={`unfav-${h.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                ) : tab === "reviews" ? (
                    reviews.length === 0 ? (
                        <div className="border border-dashed border-border_green p-12 text-center" data-testid="my-reviews-empty">
                            <MessageSquare className="w-12 h-12 text-gold/60 mx-auto mb-3" />
                            <div className="font-serif text-xl mb-1">Henüz yorum yazmadınız</div>
                            <p className="text-sm text-muted_green">Konaklamadan sonra yorumunuzu otel sayfasından paylaşabilirsiniz.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {reviews.map((r) => (
                                <div key={r.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`my-review-${r.id}`}>
                                    <div className="flex items-start justify-between flex-wrap gap-3">
                                        <div className="min-w-0">
                                            <Link to={`/hotels/${r.hotel_id}`} className="font-serif text-xl hover:text-gold transition-colors">{r.hotel_name}</Link>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="bg-gold text-emerald_deep font-bold text-xs px-2 py-0.5 rounded-sm font-mono">{r.rating}.0 / 10</span>
                                                <StatusBadge status={r.status} />
                                                <span className="text-[10px] text-muted_green">· {new Date(r.created_at).toLocaleDateString("tr-TR")}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-3 text-sm leading-relaxed text-bone/90 whitespace-pre-wrap">{r.comment}</p>
                                    {r.admin_reply && (
                                        <div className="mt-3 border-l-2 border-gold/50 pl-3 bg-emerald_deep/30 p-2.5">
                                            <div className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold mb-1">Otel Cevabı</div>
                                            <p className="text-xs text-bone/85 whitespace-pre-wrap">{r.admin_reply}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className="border border-border_green bg-emerald_surface p-6 max-w-xl space-y-4" data-testid="profile-form">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Bilgilerim</div>
                        <div>
                            <label className="label-dark flex items-center gap-2"><User className="w-3.5 h-3.5 text-gold" /> Ad Soyad</label>
                            <input className="input-dark" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="profile-name" />
                        </div>
                        <div>
                            <label className="label-dark flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gold" /> Telefon</label>
                            <input className="input-dark" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="profile-phone" />
                        </div>
                        <div>
                            <label className="label-dark flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gold" /> Yeni Şifre (opsiyonel)</label>
                            <input type="password" className="input-dark" value={profileForm.password} onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })} placeholder="Şifreyi değiştirmek için doldurun" data-testid="profile-password" />
                        </div>
                        <button onClick={saveProfile} className="btn-gold !text-xs" data-testid="profile-save">
                            <Save className="w-3.5 h-3.5" /> Bilgileri Kaydet
                        </button>
                    </div>
                )}
            </div>
        </PageTransition>
    );
};

export default Profile;
